import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, ChevronDown, LogOut, Menu, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { parseInstant } from "../lib/dateTime";
import { supabase } from "../supabaseClient";
import { useAdminNotifications } from "./useAdminNotifications";
import { getAdminUser } from "./adminAuth";
import { apiUrl } from "../utils/apiBaseUrl";
import { toggleAdminMobileNav } from "./adminMobileNav";

const AdminTopBar = ({ searchValue = "", onSearchChange, searchPlaceholder = "Search..." }) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  const [adminProfile, setAdminProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const { notifications, unreadCount, markAllAsRead } = useAdminNotifications();
  const hasSearch = typeof onSearchChange === "function";

  useEffect(() => {
    const fetchAdminProfile = async () => {
      const user = await getAdminUser();

      if (!user?.id) {
        setAdminProfile({ fullName: "Admin", email: "" });
        return;
      }

      const { data: userRow } = await supabase
        .from("users")
        .select("full_name, email")
        .eq("id", user.id)
        .single();

      setAdminProfile({
        fullName: userRow?.full_name || user.user_metadata?.full_name || "Admin",
        email: userRow?.email || user.email || "",
      });
    };

    fetchAdminProfile();
    window.addEventListener("admin-profile-updated", fetchAdminProfile);
    return () => {
      window.removeEventListener("admin-profile-updated", fetchAdminProfile);
    };
  }, []);

  useEffect(() => {
    if (notifOpen) {
      setVisibleCount(10);
    }
  }, [notifOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }

      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(apiUrl("/auth/admin-logout/"), {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.warn("Backend admin logout failed:", error.message);
    }

    await supabase.auth.signOut();
    localStorage.removeItem("isAdminAuthenticated");
    localStorage.removeItem("adminRole");
    navigate("/admin-login");
  };

  const initials = useMemo(() => {
    const source = adminProfile?.fullName || adminProfile?.email || "A";
    return String(source).trim().charAt(0).toUpperCase();
  }, [adminProfile]);

  const visibleNotifications = useMemo(() => {
    const toTime = (value) => {
      const parsed = parseInstant(value);
      return parsed ? parsed.getTime() : 0;
    };

    return [...notifications]
      .sort((left, right) => toTime(right.createdAt) - toTime(left.createdAt))
      .slice(0, visibleCount);
  }, [notifications, visibleCount]);

  const handleNotificationClick = (item) => {
    setNotifOpen(false);
    navigate(item.route || "/admin/dashboard");
  };

  return (
    <header className="bg-white border-b border-gray-200 px-3 sm:px-6">
      <div className="h-16 flex items-center justify-between gap-2 sm:gap-4 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={toggleAdminMobileNav}
            className="lg:hidden p-2 min-h-10 min-w-10 rounded-lg hover:bg-gray-100 text-gray-700 shrink-0"
            aria-label="Open navigation"
          >
            <Menu size={22} />
          </button>

          <div className={`min-w-0 flex-1 ${hasSearch ? "hidden sm:block max-w-xs" : ""}`}>
            {hasSearch ? (
              <input
                type="text"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-800"
              />
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 relative shrink-0">
          <div ref={notifRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setNotifOpen((prev) => !prev);
                setMenuOpen(false);
              }}
              className="relative p-2 min-h-10 min-w-10 rounded-lg hover:bg-gray-100"
              aria-label="Notifications"
            >
              <Bell size={20} className="text-gray-700" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute top-12 right-0 w-[min(20rem,calc(100vw-1.5rem))] bg-white border border-gray-200 rounded-lg shadow-xl z-40 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-gray-900">Notifications</p>
                  <button
                    type="button"
                    onClick={markAllAsRead}
                    className="text-xs text-brand-maroon font-bold hover:underline shrink-0"
                  >
                    Mark all as read
                  </button>
                </div>

                <div className="max-h-72 overflow-y-auto custom-scrollbar">
                  {notifications.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-400 text-center">No notifications yet.</p>
                  ) : (
                    visibleNotifications.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleNotificationClick(item)}
                        className="w-full text-left px-4 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50"
                      >
                        <div className="flex items-start justify-between gap-2 min-w-0">
                          <p
                            className={`text-sm break-words min-w-0 ${
                              item.read ? "text-gray-600" : "text-gray-900 font-semibold"
                            }`}
                          >
                            {item.title}
                          </p>
                          {!item.read && <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 break-words">{item.meta}</p>
                      </button>
                    ))
                  )}

                  {notifications.length > visibleCount && (
                    <div className="p-2 border-t border-gray-100 bg-white">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + 10)}
                        className="w-full text-sm font-semibold text-brand-maroon hover:underline py-1"
                      >
                        Load 10 more
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setMenuOpen((prev) => !prev);
                setNotifOpen(false);
              }}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 min-h-10 hover:bg-gray-100"
              aria-label="Account menu"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 font-bold text-xs flex items-center justify-center">
                {initials}
              </div>
              <ChevronDown size={16} className="text-gray-500 hidden sm:block" />
            </button>

            {menuOpen && (
              <div className="absolute top-12 right-0 w-[min(11rem,calc(100vw-1.5rem))] bg-white border border-gray-200 rounded-lg shadow-xl z-40 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/admin/profile");
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 min-h-10 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <UserCog size={16} /> Edit Profile
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 min-h-10 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasSearch ? (
        <div className="pb-3 sm:hidden">
          <input
            type="text"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-800"
          />
        </div>
      ) : null}
    </header>
  );
};

export default AdminTopBar;
